import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { axiosAuth } from '../contexts/AuthContext'; // Import authenticated axios
import logger from '../utils/logger';
import BulletinsTab from '../components/BulletinsTab';
import UploadsTab from '../components/UploadsTab';
import CreditsTab from '../components/CreditsTab';
import LiveViewersBadge from '../components/LiveViewersBadge';
import InvestorReportTab from '../components/InvestorReportTab';
import AlbumsTab from '../components/AlbumsTab';
import ReferralsTab from '../components/admin/ReferralsTab';
import { UsersTab, OrdersTab, RevenueTab } from '../components/AdminPeopleTabs';
import { 
  Music, Video, Package, Settings, BarChart3, Users, Gift, 
  Plus, Edit, Trash2, DollarSign, Eye, TrendingUp, Upload, X, Check, Image, Radio, ExternalLink, FileText, Archive, Wrench, Coins, Disc3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [collections, setCollections] = useState([]);
  const [perks, setPerks] = useState([]);
  const [beats, setBeats] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [showPerkModal, setShowPerkModal] = useState(false);
  const [showBeatModal, setShowBeatModal] = useState(false);
  const [showAdSlotModal, setShowAdSlotModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Ad management state
  const [adSlots, setAdSlots] = useState([]);
  const [adBookings, setAdBookings] = useState([]);
  
  // Downloads state
  const [pdfDownloads, setPdfDownloads] = useState([]);

  // Define fetchData BEFORE useEffect that calls it
  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, tracksRes, videosRes, collectionsRes, perksRes, beatsRes, settingsRes] = await Promise.all([
        axiosAuth.get('/api/admin/analytics'),
        axios.get(`${API}/api/music/tracks`),
        axios.get(`${API}/api/videos`),
        axios.get(`${API}/api/collections`),
        axiosAuth.get('/api/admin/perks'),
        axios.get(`${API}/api/beats`),
        axiosAuth.get('/api/admin/settings')
      ]);
      setAnalytics(analyticsRes.data);
      setTracks(tracksRes.data);
      setVideos(videosRes.data);
      setCollections(collectionsRes.data);
      setPerks(perksRes.data);
      setBeats(beatsRes.data);
      setSiteSettings(settingsRes.data || {});
      
      // Fetch ad slots and bookings
      const adSlotsRes = await axios.get(`${API}/api/ads/slots`);
      const adBookingsRes = await axiosAuth.get(`${API}/api/admin/ad-bookings`);
      setAdSlots(adSlotsRes.data);
      setAdBookings(adBookingsRes.data);
      
      // Fetch PDF downloads
      const downloadsRes = await axios.get(`${API}/api/downloads`);
      setPdfDownloads(downloadsRes.data);
    } catch (e) {
      logger.error('Failed to load admin data', e);
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, axios, API]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate, fetchData]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'report', label: 'Investor Report', icon: TrendingUp },
    { id: 'music', label: 'Music', icon: Music },
    { id: 'albums', label: 'Albums', icon: Disc3 },
    { id: 'uploads', label: 'Uploads', icon: Wrench },
    { id: 'credits', label: 'Credits', icon: Coins },
    { id: 'beats', label: 'Beats', icon: Radio },
    { id: 'bulletins', label: 'Bulletins', icon: FileText },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'downloads', label: 'PDF Books', icon: Package },
    { id: 'ads', label: 'Ad Slots', icon: DollarSign },
    { id: 'collections', label: 'Collections', icon: Package },
    { id: 'perks', label: 'Perks', icon: Gift },
    { id: 'referrals', label: 'Referrals', icon: ExternalLink },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Admin Dashboard</p>
              <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl">MANAGE YOUR PLATFORM</h1>
            </div>
            <a
              href="https://www.emergentagent.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-[#1A1A1A] border border-[#D4AF37]/30 text-[#D4AF37] text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors flex items-center gap-2"
            >
              <ExternalLink size={16} />
              Contact Emergent Support
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-8 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm uppercase tracking-wider whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-[#1A1A1A] text-white hover:bg-[#262626]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Live viewers — at-a-glance audience right now */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-[#A3A3A3]">Real-time audience on iamhethatis.com:</p>
              <LiveViewersBadge variant="inline" />
            </div>
            {/* Stats Grid — each card is tappable and jumps to its detail tab */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button
                onClick={() => setActiveTab('music')}
                className="text-left bg-[#1A1A1A] border border-white/10 p-6 hover:border-[#D4AF37] hover:bg-[#1F1A0E] active:scale-[0.98] transition-all cursor-pointer"
                data-testid="stat-card-tracks"
                aria-label="Open Music tab"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Music size={20} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#A3A3A3] uppercase tracking-wider">Tracks</span>
                </div>
                <p className="font-['Bebas_Neue'] text-4xl">{analytics?.total_tracks || 0}</p>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-2 inline-block">Tap to manage →</span>
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className="text-left bg-[#1A1A1A] border border-white/10 p-6 hover:border-[#D4AF37] hover:bg-[#1F1A0E] active:scale-[0.98] transition-all cursor-pointer"
                data-testid="stat-card-users"
                aria-label="Open Users tab"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Users size={20} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#A3A3A3] uppercase tracking-wider">Users</span>
                </div>
                <p className="font-['Bebas_Neue'] text-4xl">{analytics?.total_users || 0}</p>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-2 inline-block">Tap to view →</span>
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className="text-left bg-[#1A1A1A] border border-white/10 p-6 hover:border-[#D4AF37] hover:bg-[#1F1A0E] active:scale-[0.98] transition-all cursor-pointer"
                data-testid="stat-card-orders"
                aria-label="Open Orders tab"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Package size={20} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#A3A3A3] uppercase tracking-wider">Orders</span>
                </div>
                <p className="font-['Bebas_Neue'] text-4xl">{analytics?.total_orders || 0}</p>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-2 inline-block">Tap to view →</span>
              </button>
              <button
                onClick={() => setActiveTab('revenue')}
                className="text-left bg-[#1A1A1A] border border-white/10 p-6 hover:border-[#D4AF37] hover:bg-[#1F1A0E] active:scale-[0.98] transition-all cursor-pointer"
                data-testid="stat-card-revenue"
                aria-label="Open Revenue tab"
              >
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign size={20} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#A3A3A3] uppercase tracking-wider">Revenue</span>
                </div>
                <p className="font-['Bebas_Neue'] text-4xl">${analytics?.total_revenue?.toLocaleString() || 0}</p>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-2 inline-block">Tap to view →</span>
              </button>
            </div>

            {/* Top Tracks */}
            <div className="bg-[#1A1A1A] border border-white/10 p-6">
              <h3 className="font-['Bebas_Neue'] text-xl mb-4 flex items-center gap-2">
                <TrendingUp size={20} className="text-[#D4AF37]" />
                TOP PERFORMING TRACKS
              </h3>
              <div className="space-y-3">
                {analytics?.top_tracks?.map((track, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-[#D4AF37] font-['Bebas_Neue'] text-lg w-6">{idx + 1}</span>
                      <span>{track.title}</span>
                    </div>
                    <span className="text-[#A3A3A3] flex items-center gap-1">
                      <Eye size={14} />
                      {track.play_count?.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Music Tab */}
        {activeTab === 'music' && (
          <div>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
              <h2 className="font-['Bebas_Neue'] text-2xl">MANAGE TRACKS</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    if (!window.confirm('Smart-map tracks to Shopify products. This will also AUTO-CORRECT minor title mismatches (e.g. "Burned Bridges" → "Burning Bridges", "Living God" → "The Living God"). Continue?')) return;
                    try {
                      const { data } = await axiosAuth.post('/api/admin/tracks/shopify/auto-map');
                      const t = data.totals || {};
                      const renames = data.renamed_and_matched || [];
                      const review = data.needs_review || [];
                      const unmatched = data.unmatched || [];
                      window.alert(
                        `Matched (exact):            ${t.matched || 0}\n` +
                        `Renamed + matched:         ${t.renamed_and_matched || 0}\n` +
                        `Already set:               ${t.already_set || 0}\n` +
                        `Needs review (low conf.):  ${t.needs_review || 0}\n` +
                        `Unmatched:                 ${t.unmatched || 0}\n` +
                        (renames.length ? `\nRenamed titles:\n• ${renames.slice(0,10).map(r => `${r.old_title} → ${r.new_title}`).join('\n• ')}` : '') +
                        (review.length ? `\n\nNeeds review (open admin to confirm):\n• ${review.slice(0,8).map(r => `${r.title} → ${r.suggested_title} (${r.confidence})`).join('\n• ')}` : '') +
                        (unmatched.length ? `\n\nUnmatched (create Shopify product OR paste ID manually):\n• ${unmatched.slice(0,15).map(u => u.title).join('\n• ')}` : '\n\nAll tracks mapped.')
                      );
                      fetchData();
                    } catch (e) {
                      window.alert(`Auto-map failed: ${(e.response && e.response.data && e.response.data.detail) || e.message}`);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-[#1A1A1A] border border-[#D4AF37]/40 text-[#D4AF37] text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="auto-map-shopify-btn"
                  title="Smart match + rename to Shopify products by title"
                >
                  Auto-Map to Shopify
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Import every song from your Shopify store as a track on the site. Existing tracks (matched by title) are skipped. Continue?')) return;
                    try {
                      const { data } = await axiosAuth.post('/api/admin/tracks/shopify/bulk-import');
                      const totals = data.totals || {};
                      const created = totals.created || 0;
                      const skippedExisting = totals.skipped_existing || 0;
                      const skippedNonSong = totals.skipped_non_song || 0;
                      const totalProducts = totals.total_shopify_products || 0;
                      window.alert(
                        `Shopify products scanned: ${totalProducts}\n` +
                        `Created (new tracks):     ${created}\n` +
                        `Skipped (already on site): ${skippedExisting}\n` +
                        `Skipped (non-song items):  ${skippedNonSong}\n\n` +
                        (created > 0 ? `${created} new track${created === 1 ? '' : 's'} added — refresh to see them.` : 'No new songs to import.')
                      );
                      fetchData();
                    } catch (e) {
                      window.alert(`Bulk import failed: ${(e.response && e.response.data && e.response.data.detail) || e.message}`);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-[#1A1A1A] border border-[#D4AF37]/40 text-[#D4AF37] text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="bulk-import-shopify-btn"
                  title="Import every song from your Shopify store as a track on the site (skips duplicates)"
                >
                  Bulk Import from Shopify
                </button>
                <button
                  onClick={() => { setEditingItem(null); setShowTrackModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
                  data-testid="add-track-btn"
                >
                  <Plus size={18} />
                  Add Track
                </button>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border border-white/10">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-sm text-[#A3A3A3] uppercase tracking-wider">
                <div className="col-span-4">Title</div>
                <div className="col-span-2">Genre</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-2">Plays</div>
                <div className="col-span-2">Actions</div>
              </div>
              {tracks.map(track => (
                <div key={track.id} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 items-center hover:bg-[#262626]/50">
                  <div className="col-span-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#262626] flex-shrink-0">
                      {track.cover_art_url && (
                        <img src={track.cover_art_url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <span className="truncate">{track.title}</span>
                  </div>
                  <div className="col-span-2 text-[#A3A3A3]">{track.genre}</div>
                  <div className="col-span-2">
                    <span className="px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-sm">
                      ${track.price}
                    </span>
                  </div>
                  <div className="col-span-2 text-[#A3A3A3]">{track.play_count?.toLocaleString() || 0}</div>
                  <div className="col-span-2 flex gap-2">
                    <button
                      onClick={() => { setEditingItem(track); setShowTrackModal(true); }}
                      className="p-2 bg-[#262626] hover:bg-[#D4AF37] hover:text-black transition-colors"
                      data-testid={`edit-track-${track.id}`}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteTrack(track.id)}
                      className="p-2 bg-[#262626] hover:bg-red-600 transition-colors"
                      data-testid={`delete-track-${track.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Bulletins Tab */}
        {activeTab === 'bulletins' && <BulletinsTab />}
        {activeTab === 'uploads' && <UploadsTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'orders' && <OrdersTab />}
        {activeTab === 'revenue' && <RevenueTab />}
        {activeTab === 'credits' && <CreditsTab />}
        {activeTab === 'report' && <InvestorReportTab />}
        {activeTab === 'albums' && <AlbumsTab />}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Bebas_Neue'] text-2xl">MANAGE VIDEOS</h2>
              <button
                onClick={() => { setEditingItem(null); setShowVideoModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
                data-testid="add-video-btn"
              >
                <Plus size={18} />
                Add Video
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map(video => (
                <div key={video.id} className="bg-[#1A1A1A] border border-white/10 overflow-hidden group">
                  <div className="aspect-video bg-[#262626] relative">
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video size={32} className="text-[#737373]" />
                      </div>
                    )}
                    {video.is_exclusive && (
                      <span className="absolute top-2 right-2 px-2 py-1 bg-[#D4AF37] text-black text-xs uppercase">
                        Exclusive
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium truncate">{video.title}</h3>
                    <p className="text-sm text-[#A3A3A3] mt-1">{video.category}</p>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => { setEditingItem(video); setShowVideoModal(true); }}
                        className="flex-1 p-2 bg-[#262626] hover:bg-[#D4AF37] hover:text-black transition-colors text-center"
                      >
                        <Edit size={16} className="mx-auto" />
                      </button>
                      <button
                        onClick={() => handleDeleteVideo(video.id)}
                        className="flex-1 p-2 bg-[#262626] hover:bg-red-600 transition-colors text-center"
                      >
                        <Trash2 size={16} className="mx-auto" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Beats Tab */}
        {activeTab === 'beats' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Bebas_Neue'] text-2xl">BEATS LICENSING</h2>
              <button
                onClick={() => { setEditingItem(null); setShowBeatModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
              >
                <Plus size={18} />
                Add Beat
              </button>
            </div>

            <div className="bg-[#1A1A1A] rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-sm font-medium text-[#A3A3A3] uppercase tracking-wider">
                <div className="col-span-3">Beat</div>
                <div className="col-span-2">BPM / Key</div>
                <div className="col-span-2">Basic</div>
                <div className="col-span-2">Premium</div>
                <div className="col-span-2">Exclusive</div>
                <div className="col-span-1">Actions</div>
              </div>
              {beats.map(beat => (
                <div key={beat.id} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 items-center hover:bg-[#262626]/50">
                  <div className="col-span-3 flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#262626] flex-shrink-0">
                      {beat.cover_art_url && (
                        <img src={beat.cover_art_url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <span className="truncate">{beat.title}</span>
                  </div>
                  <div className="col-span-2 text-[#A3A3A3] text-sm">
                    {beat.bpm && `${beat.bpm} BPM`}
                    {beat.key && <div className="text-xs">{beat.key}</div>}
                  </div>
                  <div className="col-span-2">
                    <span className="px-2 py-1 bg-green-600/20 text-green-400 text-sm">
                      ${beat.basic_lease_price?.toFixed(2) || '29.99'}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="px-2 py-1 bg-blue-600/20 text-blue-400 text-sm">
                      ${beat.premium_lease_price?.toFixed(2) || '99.99'}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-sm">
                      ${beat.exclusive_rights_price?.toFixed(2) || '299.99'}
                    </span>
                  </div>
                  <div className="col-span-1 flex gap-2">
                    <button
                      onClick={() => { setEditingItem(beat); setShowBeatModal(true); }}
                      className="p-2 bg-[#262626] hover:bg-[#D4AF37] hover:text-black transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteBeat(beat.id)}
                      className="p-2 bg-[#262626] hover:bg-red-600 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Collections Tab */}
        {activeTab === 'collections' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Bebas_Neue'] text-2xl">THEMED COLLECTIONS</h2>
              <button
                onClick={() => { setEditingItem(null); setShowCollectionModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
                data-testid="add-collection-btn"
              >
                <Plus size={18} />
                Create Collection
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {collections.map(col => (
                <div key={col.id} className="bg-[#1A1A1A] border border-white/10 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-['Bebas_Neue'] text-xl">{col.name}</h3>
                      <p className="text-sm text-[#A3A3A3] mt-1">{col.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingItem(col); setShowCollectionModal(true); }}
                        className="p-2 hover:bg-[#D4AF37]/20 text-[#D4AF37] transition-colors"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm('Delete this collection?')) {
                            await axiosAuth.delete(`${API}/api/admin/collections/${col.id}`);
                            fetchData();
                          }
                        }}
                        className="p-2 hover:bg-red-500/20 text-red-400 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Ad Slots Tab */}
        {activeTab === 'ads' && (
          <div>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
              <h2 className="font-['Bebas_Neue'] text-2xl">AD SLOT MANAGEMENT</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    if (!window.confirm('Map every ad slot to its Shopify Buy Button product ID (Top Banner, Homepage Hero, Beats Header, Sponsored Book Card). Continue?')) return;
                    try {
                      const { data } = await axiosAuth.post('/api/admin/ad-slots/shopify/auto-map');
                      const t = data.totals || {};
                      const matched = data.matched || [];
                      const unknown = data.unknown_placement || [];
                      window.alert(
                        `Matched:              ${t.matched || 0}\n` +
                        `Already set:          ${t.already_set || 0}\n` +
                        `Unknown placement:    ${t.unknown_placement || 0}\n` +
                        (matched.length ? `\nJust mapped:\n• ${matched.map(m => `${m.slot_name} → ${m.product_id}`).join('\n• ')}` : '') +
                        (unknown.length ? `\n\nUnknown placement keys (no mapping):\n• ${unknown.map(u => `${u.slot_name} (${u.placement})`).join('\n• ')}` : '')
                      );
                      fetchData();
                    } catch (e) {
                      window.alert(`Ad slot auto-map failed: ${(e.response && e.response.data && e.response.data.detail) || e.message}`);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-[#1A1A1A] border border-[#D4AF37]/40 text-[#D4AF37] text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="auto-map-adslots-shopify-btn"
                  title="Map every ad slot to its Shopify Buy Button product ID"
                >
                  Auto-Map Ad Slots to Shopify
                </button>
                <button
                  onClick={() => { setEditingItem(null); setShowAdSlotModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
                >
                  <Plus size={18} />
                  Create Ad Slot
                </button>
              </div>
            </div>

            <AdsControlPanel API={API} onChange={fetchData} />

            {/* Ad Slots Table */}
            <div className="bg-[#1A1A1A] rounded-lg overflow-hidden mb-8">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-sm font-medium text-[#A3A3A3] uppercase tracking-wider">
                <div className="col-span-3">Slot Name</div>
                <div className="col-span-2">Placement</div>
                <div className="col-span-2">Dimensions</div>
                <div className="col-span-1">Price/Month</div>
                <div className="col-span-2">Availability</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1">Actions</div>
              </div>
              
              {adSlots.map((slot) => (
                <div key={slot.id} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 hover:bg-white/5">
                  <div className="col-span-3 text-white font-medium">{slot.slot_name}</div>
                  <div className="col-span-2 text-sm text-[#A3A3A3]">{slot.placement}</div>
                  <div className="col-span-2 text-sm text-[#A3A3A3]">{slot.dimensions}</div>
                  <div className="col-span-1 text-[#D4AF37] font-medium">${slot.price_per_month}</div>
                  <div className="col-span-2">
                    <span className={`text-sm ${slot.available_slots > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {slot.available_slots} / {slot.max_slots} available
                    </span>
                  </div>
                  <div className="col-span-1">
                    <span className={`px-2 py-1 text-xs rounded ${slot.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {slot.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="col-span-1 flex gap-2">
                    <button
                      onClick={() => { setEditingItem(slot); setShowAdSlotModal(true); }}
                      className="p-2 hover:bg-[#D4AF37]/20 text-[#D4AF37] transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Delete this ad slot?')) {
                          await axiosAuth.delete(`${API}/api/admin/ad-slots/${slot.id}`);
                          fetchData();
                        }
                      }}
                      className="p-2 hover:bg-red-500/20 text-red-400 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Ad Bookings Section */}
            <h3 className="font-['Bebas_Neue'] text-xl mb-4">PENDING BOOKINGS</h3>
            <div className="bg-[#1A1A1A] rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-sm font-medium text-[#A3A3A3] uppercase tracking-wider">
                <div className="col-span-2">Brand</div>
                <div className="col-span-2">Slot</div>
                <div className="col-span-2">Duration</div>
                <div className="col-span-1">Price</div>
                <div className="col-span-2">Contact</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2">Actions</div>
              </div>
              
              {adBookings.filter(b => b.status === 'pending').map((booking) => (
                <div key={booking.id} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5">
                  <div className="col-span-2 text-white font-medium">
                    <div>{booking.brand_name}</div>
                    {booking.ad_image_url && (
                      <a href={booking.ad_image_url} target="_blank" rel="noopener noreferrer" className="block mt-2">
                        <img src={booking.ad_image_url} alt="creative preview" className="max-h-12 rounded border border-[#262626]" />
                      </a>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-[#A3A3A3]">
                    <div>{booking.slot_name}</div>
                    {booking.headline && <div className="text-xs text-white mt-1 truncate">"{booking.headline}"</div>}
                  </div>
                  <div className="col-span-2 text-sm text-[#A3A3A3]">
                    {booking.start_date} to {booking.end_date}
                  </div>
                  <div className="col-span-1 text-[#D4AF37]">${booking.total_price}</div>
                  <div className="col-span-2 text-sm text-[#A3A3A3]">
                    <div>{booking.contact_email}</div>
                    {booking.contact_name && <div className="text-xs text-[#737373] mt-1">{booking.contact_name}</div>}
                  </div>
                  <div className="col-span-1 flex flex-col gap-1">
                    <span className="px-2 py-1 text-xs rounded bg-yellow-500/20 text-yellow-400 text-center">
                      Pending
                    </span>
                    {booking.payment_status && booking.payment_status !== 'unpaid' && (
                      <span className="px-2 py-1 text-[10px] rounded bg-blue-500/20 text-blue-400 text-center">
                        {booking.payment_status}
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 flex gap-1 flex-wrap">
                    <button
                      onClick={async () => {
                        await axiosAuth.put(`${API}/api/admin/ad-bookings/${booking.id}/approve`);
                        fetchData();
                        alert('Booking approved! Will go live once payment_status is "paid".');
                      }}
                      className="flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 text-sm"
                    >
                      <Check size={14} />
                      Approve
                    </button>
                    <button
                      onClick={async () => {
                        const reason = prompt('Rejection reason (optional):');
                        await axiosAuth.put(`${API}/api/admin/ad-bookings/${booking.id}/reject`, { reason });
                        fetchData();
                        alert('Booking rejected');
                      }}
                      className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 text-sm"
                    >
                      <X size={14} />
                      Reject
                    </button>
                    <button
                      title="Delete (use for tests/spam)"
                      onClick={async () => {
                        if (!window.confirm(`PERMANENTLY DELETE booking from "${booking.brand_name}"?`)) return;
                        await axiosAuth.delete(`${API}/api/admin/ad-bookings/${booking.id}`);
                        fetchData();
                      }}
                      className="flex items-center gap-1 px-2 py-1 bg-[#262626] text-[#A3A3A3] rounded hover:bg-[#333] text-sm"
                      data-testid={`delete-pending-booking-${booking.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              
              {adBookings.filter(b => b.status === 'pending').length === 0 && (
                <div className="p-8 text-center text-[#737373]">
                  No pending bookings
                </div>
              )}
            </div>

            {/* Approved/Active Bookings */}
            {adBookings.filter(b => b.status === 'approved').length > 0 && (
              <>
                <h3 className="font-['Bebas_Neue'] text-xl mt-10 mb-4">APPROVED BOOKINGS</h3>
                <div className="bg-[#1A1A1A] rounded-lg overflow-hidden">
                  <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-sm font-medium text-[#A3A3A3] uppercase tracking-wider">
                    <div className="col-span-2">Brand</div>
                    <div className="col-span-2">Slot</div>
                    <div className="col-span-2">Duration</div>
                    <div className="col-span-1">Price</div>
                    <div className="col-span-2">Payment</div>
                    <div className="col-span-2">Live Status</div>
                    <div className="col-span-1 text-right">Actions</div>
                  </div>
                  {adBookings.filter(b => b.status === 'approved').map((booking) => (
                    <div key={booking.id} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 items-center">
                      <div className="col-span-2 text-white font-medium">{booking.brand_name}</div>
                      <div className="col-span-2 text-sm text-[#A3A3A3]">{booking.slot_name}</div>
                      <div className="col-span-2 text-sm text-[#A3A3A3]">{booking.start_date} → {booking.end_date}</div>
                      <div className="col-span-1 text-[#D4AF37]">${booking.total_price}</div>
                      <div className="col-span-2">
                        <select
                          value={booking.payment_status || 'unpaid'}
                          onChange={async (e) => {
                            await axiosAuth.put(`${API}/api/admin/ad-bookings/${booking.id}/status`, { payment_status: e.target.value });
                            fetchData();
                          }}
                          className="bg-[#0F0F0F] border border-[#262626] rounded px-2 py-1 text-sm text-white w-full"
                        >
                          <option value="unpaid">Unpaid</option>
                          <option value="paid">Paid</option>
                          <option value="refund_pending">Refund Pending</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <select
                          value={booking.live_status || 'scheduled'}
                          onChange={async (e) => {
                            await axiosAuth.put(`${API}/api/admin/ad-bookings/${booking.id}/status`, { live_status: e.target.value });
                            fetchData();
                          }}
                          className="bg-[#0F0F0F] border border-[#262626] rounded px-2 py-1 text-sm text-white w-full"
                        >
                          <option value="scheduled">Scheduled</option>
                          <option value="live">Live</option>
                          <option value="paused">Paused</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>
                      <div className="col-span-1 flex gap-1 justify-end">
                        <button
                          title="Archive (hide but keep records)"
                          onClick={async () => {
                            if (!window.confirm(`Archive booking from "${booking.brand_name}"? This will hide it from the live site.`)) return;
                            await axiosAuth.put(`${API}/api/admin/ad-bookings/${booking.id}/archive`);
                            fetchData();
                          }}
                          className="p-2 text-yellow-400 hover:bg-yellow-500/20 rounded"
                          data-testid={`archive-booking-${booking.id}`}
                        >
                          <Archive size={16} />
                        </button>
                        <button
                          title="Delete permanently"
                          onClick={async () => {
                            if (!window.confirm(`PERMANENTLY DELETE booking from "${booking.brand_name}"? This cannot be undone.`)) return;
                            await axiosAuth.delete(`${API}/api/admin/ad-bookings/${booking.id}`);
                            fetchData();
                          }}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded"
                          data-testid={`delete-booking-${booking.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Downloads Tab */}
        {activeTab === 'downloads' && (
          <div>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
              <h2 className="font-['Bebas_Neue'] text-2xl">PDF DOWNLOADS MANAGEMENT</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    if (!window.confirm('Smart-map books to Shopify products. Will auto-correct minor title differences (e.g. missing "The"). Continue?')) return;
                    try {
                      const { data } = await axiosAuth.post('/api/admin/books/shopify/auto-map');
                      const t = data.totals || {};
                      const renames = data.renamed_and_matched || [];
                      const review = data.needs_review || [];
                      const unmatched = data.unmatched || [];
                      window.alert(
                        `Matched (exact):           ${t.matched || 0}\n` +
                        `Renamed + matched:         ${t.renamed_and_matched || 0}\n` +
                        `Already set:               ${t.already_set || 0}\n` +
                        `Needs review (low conf.):  ${t.needs_review || 0}\n` +
                        `Unmatched:                 ${t.unmatched || 0}\n` +
                        (renames.length ? `\nRenamed titles:\n• ${renames.slice(0,10).map(r => `${r.old_title} → ${r.new_title}`).join('\n• ')}` : '') +
                        (review.length ? `\n\nNeeds review:\n• ${review.slice(0,8).map(r => `${r.title} → ${r.suggested_title} (${r.confidence})`).join('\n• ')}` : '') +
                        (unmatched.length ? `\n\nUnmatched (create Shopify product OR paste ID manually):\n• ${unmatched.slice(0,15).map(u => u.title).join('\n• ')}` : '\n\nAll books mapped.')
                      );
                      fetchData();
                    } catch (e) {
                      window.alert(`Book auto-map failed: ${(e.response && e.response.data && e.response.data.detail) || e.message}`);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-[#1A1A1A] border border-[#D4AF37]/40 text-[#D4AF37] text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="auto-map-books-shopify-btn"
                  title="Smart match books to Shopify products by title"
                >
                  Auto-Map Books to Shopify
                </button>
                <button
                  onClick={() => { setEditingItem(null); setShowDownloadModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
                >
                  <Plus size={18} />
                  Add PDF Download
                </button>
              </div>
            </div>

            {/* Downloads Table */}
            <div className="bg-[#1A1A1A] rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-sm font-medium text-[#A3A3A3] uppercase tracking-wider">
                <div className="col-span-3">Title</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-3">Description</div>
                <div className="col-span-1">Size (MB)</div>
                <div className="col-span-1">Downloads</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1">Actions</div>
              </div>
              
              {pdfDownloads.map((download) => (
                <div key={download.id} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 hover:bg-white/5">
                  <div className="col-span-3 text-white font-medium">{download.title}</div>
                  <div className="col-span-2 text-sm text-[#D4AF37]">{download.category}</div>
                  <div className="col-span-3 text-sm text-[#A3A3A3] truncate">{download.description || 'No description'}</div>
                  <div className="col-span-1 text-sm text-[#A3A3A3]">{download.file_size_mb?.toFixed(1) || '-'}</div>
                  <div className="col-span-1 text-sm text-[#A3A3A3]">{download.download_count}</div>
                  <div className="col-span-1">
                    <span className={`px-2 py-1 text-xs rounded ${download.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {download.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="col-span-1 flex flex-col gap-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingItem(download); setShowDownloadModal(true); }}
                        className="p-2 hover:bg-[#D4AF37]/20 text-[#D4AF37] transition-colors"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm('Delete this download?')) {
                            await axiosAuth.delete(`${API}/api/admin/downloads/${download.id}`);
                            fetchData();
                          }
                        }}
                        className="p-2 hover:bg-red-500/20 text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {/* Preview Watermark Button */}
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/api/downloads/${download.id}/preview-watermark`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                          });
                          
                          if (!response.ok) throw new Error('Preview failed');
                          
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${download.title}_PREVIEW_WATERMARKED.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          alert('Failed to generate preview: ' + error.message);
                        }
                      }}
                      className="px-2 py-1 bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 text-xs rounded transition-colors w-full"
                      title="Download preview with watermark"
                    >
                      Preview Watermark
                    </button>
                  </div>
                </div>
              ))}
              
              {pdfDownloads.length === 0 && (
                <div className="p-8 text-center text-[#737373]">
                  No PDF downloads yet. Click "Add PDF Download" to get started.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Perks Tab */}
        {activeTab === 'perks' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-['Bebas_Neue'] text-2xl">MANAGE PERKS & BONUSES</h2>
              <button
                onClick={() => { setEditingItem(null); setShowPerkModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
                data-testid="add-perk-btn"
              >
                <Plus size={18} />
                Create Perk
              </button>
            </div>

            <p className="text-[#A3A3A3] mb-6">
              Create perks and attach them to tracks. Fans who purchase the track will unlock these bonuses.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {perks.map(perk => (
                <div key={perk.id} className="bg-[#1A1A1A] border border-white/10 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift size={16} className="text-[#D4AF37]" />
                    <span className="text-xs uppercase tracking-wider text-[#D4AF37]">{perk.perk_type?.replace('_', ' ')}</span>
                  </div>
                  <h3 className="font-medium">{perk.title}</h3>
                  <p className="text-sm text-[#A3A3A3] mt-1 line-clamp-2">{perk.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referrals Tab — admin-only hub for outbound referral / affiliate links */}
        {activeTab === 'referrals' && <ReferralsTab />}


        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <SettingsTab settings={siteSettings} onUpdate={fetchData} />
        )}
      </div>

      {/* Track Modal */}
      {showTrackModal && (
        <TrackModal
          track={editingItem}
          onClose={() => { setShowTrackModal(false); setEditingItem(null); }}
          onSave={fetchData}
          perks={perks}
          collections={collections}
        />
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <VideoModal
          video={editingItem}
          onClose={() => { setShowVideoModal(false); setEditingItem(null); }}
          onSave={fetchData}
          tracks={tracks}
        />
      )}

      {/* Collection Modal */}
      {showCollectionModal && (
        <CollectionModal
          collection={editingItem}
          onClose={() => { setShowCollectionModal(false); setEditingItem(null); }}
          onSave={fetchData}
          tracks={tracks}
        />
      )}

      {/* Perk Modal */}
      {showPerkModal && (
        <PerkModal
          perk={editingItem}
          onClose={() => { setShowPerkModal(false); setEditingItem(null); }}
          onSave={fetchData}
        />
      )}

      {/* Beat Modal */}
      {showBeatModal && (
        <BeatModal
          beat={editingItem}
          onClose={() => { setShowBeatModal(false); setEditingItem(null); }}
          onSave={fetchData}
        />
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <DownloadModal
          download={editingItem}
          onClose={() => { setShowDownloadModal(false); setEditingItem(null); }}
          onSave={fetchData}
        />
      )}

      {showAdSlotModal && (
        <AdSlotModal
          slot={editingItem}
          onClose={() => { setShowAdSlotModal(false); setEditingItem(null); }}
          onSave={fetchData}
        />
      )}
    </div>
  );

  async function handleDeleteTrack(id) {
    if (!window.confirm('Delete this track?')) return;
    try {
      await axiosAuth.delete(`/api/admin/tracks/${id}`);
      fetchData();
    } catch (e) {
      alert('Failed to delete');
    }
  }

  async function handleDeleteVideo(id) {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axiosAuth.delete(`/api/admin/videos/${id}`);
      fetchData();
    } catch (e) {
      alert('Failed to delete');
    }
  }

  async function handleDeleteCollection(id) {
    if (!window.confirm('Delete this collection?')) return;
    try {
      await axiosAuth.delete(`/api/admin/collections/${id}`);
      fetchData();
    } catch (e) {
      alert('Failed to delete');
    }
  }

  async function handleDeleteBeat(id) {
    if (!window.confirm('Delete this beat?')) return;
    try {
      await axiosAuth.delete(`/api/admin/beats/${id}`);
      fetchData();
    } catch (e) {
      alert('Failed to delete beat');
    }
  }
};

// Track Modal Component
const TrackModal = ({ track, onClose, onSave, perks, collections }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  
  // Helper function to normalize URLs (convert relative to absolute)
  const normalizeUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url; // Already absolute
    }
    // Relative URL - prepend API base
    return `${API}${url}`;
  };
  
  const [form, setForm] = useState({
    title: track?.title || '',
    genre: track?.genre || 'Hip Hop',
    mood: track?.mood || '',
    album: track?.album || '',
    duration: track?.duration || 180,
    price: track?.price || 1.99,
    is_new_release: track?.is_new_release ?? true,
    access_tier: track?.access_tier || 'preview',
    cover_art_url: normalizeUrl(track?.cover_art_url) || '',
    audio_url: normalizeUrl(track?.audio_url) || '',
    preview_url: normalizeUrl(track?.preview_url) || '',
    collection_id: track?.collection_id || '',
    story_behind: track?.story_behind || '',
    perks: track?.perks || [],
    preview_duration: track?.preview_duration ?? 30,
    preview_enabled: track?.preview_enabled !== false,
    shopify_product_id: track?.shopify_product_id || '',
    shopify_variant_id: track?.shopify_variant_id || ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit for images)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert(`File too large. Maximum size is 10MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    setUploadingCover(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axiosAuth.post(`/api/admin/upload/cover`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000, // 1 minute timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          logger.debug(`Cover upload progress: ${percentCompleted}%`);
        }
      });
      // R2 URLs are already full URLs, don't prepend API
      const fullUrl = data.url.startsWith('http') ? data.url : `${API}${data.url}`;
      setForm(prev => ({ ...prev, cover_art_url: fullUrl }));
      alert('Cover art uploaded successfully!');
    } catch (err) {
      logger.error('Cover upload error:', err);
      if (err.code === 'ECONNABORTED') {
        alert('Upload timeout. Please try with a smaller file or check your internet connection.');
      } else {
        alert('Failed to upload cover: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // iOS-friendly format validation by extension (some iOS Safari versions
    // give an empty MIME type, so we cannot rely solely on file.type).
    const allowedExts = ['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg', 'oga'];
    const fname = (file.name || '').toLowerCase();
    const ext = fname.includes('.') ? fname.split('.').pop() : '';
    const mime = (file.type || '').toLowerCase();
    const looksLikeAudio = mime.startsWith('audio/') || allowedExts.includes(ext);
    if (!looksLikeAudio) {
      alert('This audio format is not supported on iPhone. Please upload MP3, M4A, AAC, or WAV.');
      e.target.value = '';
      return;
    }

    // Check file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      alert(`File too large. Maximum size is 50MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    setUploadingAudio(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axiosAuth.post(`/api/admin/upload/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minutes timeout for large files
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          logger.debug(`Upload progress: ${percentCompleted}%`);
        }
      });
      // R2 URLs are already full URLs, don't prepend API
      const fullUrl = data.url.startsWith('http') ? data.url : `${API}${data.url}`;
      logger.debug('✅ Audio uploaded, URL:', fullUrl);
      setForm(prev => ({ ...prev, audio_url: fullUrl }));
      
      // Force audio element to reload by triggering a re-render
      setTimeout(() => {
        const audioElement = document.querySelector('audio');
        if (audioElement) {
          audioElement.load();
          logger.debug('🎵 Audio element reloaded');
        }
      }, 100);
      alert('Audio uploaded successfully!');
    } catch (err) {
      logger.error('Audio upload error:', err);
      if (err.code === 'ECONNABORTED') {
        alert('Upload timeout. Please try with a smaller file or check your internet connection.');
      } else {
        alert('Failed to upload audio: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    logger.debug('🎵 Submitting track with form data:', {
      title: form.title,
      genre: form.genre,
      audio_url: form.audio_url,
      cover_art_url: form.cover_art_url,
      duration: form.duration,
      price: form.price
    });
    
    if (!form.audio_url) {
      alert('⚠️ Audio URL is missing! Please upload audio before saving.');
      return;
    }
    
    if (!form.title || !form.genre) {
      alert('⚠️ Please fill in required fields: Title and Genre');
      return;
    }
    
    setSaving(true);
    try {
      // Prepare data to send - only include defined fields
      const dataToSend = {
        title: form.title,
        genre: form.genre,
        duration: parseInt(form.duration) || 180,
        price: parseFloat(form.price) || 1.99,
        is_new_release: Boolean(form.is_new_release),
        access_tier: form.access_tier || "preview",
        audio_url: form.audio_url,
        preview_duration: parseInt(form.preview_duration) ?? 30,
        preview_enabled: Boolean(form.preview_enabled)
      };
      
      // Add optional fields only if they exist
      if (form.mood) dataToSend.mood = form.mood;
      if (form.album) dataToSend.album = form.album;
      if (form.cover_art_url) dataToSend.cover_art_url = form.cover_art_url;
      if (form.preview_url) dataToSend.preview_url = form.preview_url;
      if (form.collection_id) dataToSend.collection_id = form.collection_id;
      if (form.story_behind) dataToSend.story_behind = form.story_behind;
      if (form.perks && form.perks.length > 0) dataToSend.perks = form.perks;
      // Shopify mapping — allows admin to manually wire a track to its
      // Shopify product for the BUY button. Empty strings sent as null so
      // the backend can clear a previous mapping if needed.
      dataToSend.shopify_product_id = (form.shopify_product_id || '').trim() || null;
      dataToSend.shopify_variant_id = (form.shopify_variant_id || '').trim() || null;
      
      logger.debug('📤 Sending data:', dataToSend);
      
      let saveResponse;
      if (track) {
        saveResponse = await axiosAuth.put(`/api/admin/tracks/${track.id}`, dataToSend);
      } else {
        saveResponse = await axiosAuth.post(`/api/admin/tracks`, dataToSend);
      }
      // Backend returns: { id, message, audio_url }
      const savedId = saveResponse?.data?.id || track?.id;
      const savedUrl = saveResponse?.data?.audio_url || form.audio_url;

      // VISIBILITY CHECK: confirm the track is actually returned by the
      // public /api/music/tracks endpoint. This proves the save not only
      // wrote to the DB but is also visible to site visitors.
      let liveStatus = '';
      try {
        const { data: publicTracks } = await axios.get(`${API}/api/music/tracks?limit=1000`);
        const isLive = Array.isArray(publicTracks) && publicTracks.some((t) => t.id === savedId);
        if (isLive) {
          liveStatus = '\n\n🌍 LIVE: Track is visible on the public Music page now.';
        } else {
          liveStatus = '\n\n⚠️ NOT VISIBLE YET on /music. The DB row exists but the public listing did not return it. Try refreshing the public page, or check the Music tab to confirm.';
        }
      } catch (vErr) {
        liveStatus = '\n\n(Could not verify live visibility — check /music manually.)';
      }

      alert(
        `✅ Track saved successfully and linked to storage.\n\n` +
        `Track ID: ${savedId}\n` +
        `Linked audio: ${savedUrl}` +
        liveStatus
      );
      onSave();
      onClose();
    } catch (e) {
      logger.error('❌ Save error:', e);
      logger.error('❌ Error response:', e.response?.data);
      
      // Better error message handling
      let errorMsg = 'Unknown error';
      if (e.response?.data) {
        if (typeof e.response.data === 'string') {
          errorMsg = e.response.data;
        } else if (e.response.data.detail) {
          errorMsg = typeof e.response.data.detail === 'string' 
            ? e.response.data.detail 
            : JSON.stringify(e.response.data.detail);
        } else if (e.response.data.message) {
          errorMsg = e.response.data.message;
        } else {
          errorMsg = JSON.stringify(e.response.data);
        }
      } else if (e.message) {
        errorMsg = e.message;
      }
      
      alert('Failed to save track: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="font-['Bebas_Neue'] text-2xl">{track ? 'EDIT TRACK' : 'ADD NEW TRACK'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#262626] transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
                required
              />
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Genre *</label>
              <select
                value={form.genre}
                onChange={(e) => setForm({...form, genre: e.target.value})}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              >
                <option>Hip Hop</option>
                <option>Instrumental</option>
                <option>R&B</option>
                <option>Spiritual</option>
                <option>Gospel</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Mood</label>
              <select
                value={form.mood}
                onChange={(e) => setForm({...form, mood: e.target.value})}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              >
                <option value="">Select mood</option>
                <option>Uplifting</option>
                <option>Spiritual</option>
                <option>Powerful</option>
                <option>Chill</option>
                <option>Motivational</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Album</label>
              <input
                type="text"
                value={form.album}
                onChange={(e) => setForm({...form, album: e.target.value})}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              />
              <p className="text-xs text-[#737373] mt-1">
                Suggested: New releases $1.99, Catalog $0.99
              </p>
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Duration (sec)</label>
              <input
                type="number"
                value={form.duration}
                onChange={(e) => setForm({...form, duration: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_new_release}
                  onChange={(e) => {
                    const isNew = e.target.checked;
                    setForm({
                      ...form, 
                      is_new_release: isNew,
                      price: isNew ? 1.99 : 0.99
                    });
                  }}
                  className="w-5 h-5 accent-[#D4AF37]"
                />
                <span>New Release (auto-sets $1.99)</span>
              </label>
            </div>
          </div>

          {/* Preview Controls */}
          <div className="border-t border-white/10 pt-6">
            <h3 className="text-sm font-medium text-[#D4AF37] mb-4">Preview Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#A3A3A3] block mb-2">
                  Preview Duration (0-90 sec)
                </label>
                <input
                  type="range"
                  min="0"
                  max="90"
                  value={form.preview_duration || 30}
                  onChange={(e) => setForm({...form, preview_duration: parseInt(e.target.value)})}
                  className="w-full accent-[#D4AF37]"
                />
                <div className="flex justify-between text-xs text-[#737373] mt-1">
                  <span>0s (Members Only)</span>
                  <span className="text-[#D4AF37] font-medium">{form.preview_duration || 30}s</span>
                  <span>90s (Max)</span>
                </div>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.preview_enabled !== false}
                    onChange={(e) => setForm({...form, preview_enabled: e.target.checked})}
                    className="w-5 h-5 accent-[#D4AF37]"
                  />
                  <div>
                    <span className="block">Preview Enabled</span>
                    <span className="text-xs text-[#737373]">
                      {form.preview_enabled !== false ? 'Public preview available' : 'Members only (purchase required)'}
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Cover Art Upload */}
          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Cover Art</label>
            <div className="flex gap-4 items-start">
              {form.cover_art_url && (
                <div className="w-24 h-24 bg-[#262626] rounded overflow-hidden flex-shrink-0">
                  <img src={form.cover_art_url} alt="Cover" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1">
                <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed cursor-pointer transition-colors ${
                  uploadingCover ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/20 hover:border-[#D4AF37]'
                }`}>
                  <Upload size={20} />
                  <span>{uploadingCover ? 'Uploading...' : 'Upload Cover Image'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverUpload}
                    className="hidden"
                    disabled={uploadingCover}
                  />
                </label>
                <p className="text-xs text-[#737373] mt-2">JPG, PNG, WebP. Max 10MB.</p>
              </div>
            </div>
          </div>

          {/* Audio File Upload */}
          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Audio File (Full Track)</label>
            <div className="space-y-3">
              <div className="flex gap-4 items-start">
                {form.audio_url && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-600/20 rounded border border-green-600/30">
                    <Music size={16} className="text-green-400" />
                    <span className="text-sm text-green-400">✓ Audio uploaded</span>
                  </div>
                )}
                <div className="flex-1">
                  <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed cursor-pointer transition-colors ${
                    uploadingAudio ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/20 hover:border-[#D4AF37]'
                  }`}>
                    <Upload size={20} />
                    <span>{uploadingAudio ? 'Uploading...' : 'Upload Audio File'}</span>
                    <input
                      type="file"
                      accept=".mp3,.m4a,.wav,.aac,.flac,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/aac,audio/*"
                      onChange={handleAudioUpload}
                      className="hidden"
                      disabled={uploadingAudio}
                      data-testid="track-audio-upload-input"
                    />
                  </label>
                  <p className="text-xs text-[#737373] mt-2">MP3, M4A, AAC, or WAV. Max 50MB. iPhone & Android friendly.</p>
                </div>
              </div>
              
              {/* Audio Preview Player */}
              {form.audio_url && (
                <div className="bg-[#262626] p-4 rounded">
                  <p className="text-xs text-[#A3A3A3] mb-2">Preview Audio:</p>
                  <audio 
                    key={form.audio_url}
                    controls 
                    src={form.audio_url}
                    className="w-full"
                    style={{ height: '40px' }}
                    preload="metadata"
                  >
                    Your browser does not support audio playback.
                  </audio>
                  <p className="text-xs text-[#666] mt-1">URL: {form.audio_url.substring(0, 60)}...</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Collection</label>
            <select
              value={form.collection_id}
              onChange={(e) => setForm({...form, collection_id: e.target.value})}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
            >
              <option value="">No collection</option>
              {collections.map(col => (
                <option key={col.id} value={col.id}>{col.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Story Behind the Track</label>
            <textarea
              value={form.story_behind}
              onChange={(e) => setForm({...form, story_behind: e.target.value})}
              rows={3}
              placeholder="Share the inspiration or story behind this track..."
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white resize-none"
            />
          </div>

          <div className="border border-[#D4AF37]/30 rounded p-4 bg-[#D4AF37]/5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Shopify Buy Button (payment layer)</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#A3A3A3] block mb-1">Shopify Product ID</label>
                <input
                  type="text"
                  value={form.shopify_product_id || ''}
                  onChange={(e) => setForm({ ...form, shopify_product_id: e.target.value })}
                  placeholder="e.g. 15871514181713"
                  className="w-full px-3 py-2 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white text-sm font-mono"
                  data-testid="track-shopify-product-id-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] block mb-1">Shopify Variant ID (optional)</label>
                <input
                  type="text"
                  value={form.shopify_variant_id || ''}
                  onChange={(e) => setForm({ ...form, shopify_variant_id: e.target.value })}
                  placeholder="defaults to first variant"
                  className="w-full px-3 py-2 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white text-sm font-mono"
                  data-testid="track-shopify-variant-id-input"
                />
              </div>
            </div>
            <p className="text-[10px] text-[#737373] mt-2">
              When set, fans see a gold BUY button that adds this song to the Shopify cart. Find product IDs in your Shopify admin → Products → click the product → look at the URL (the last number). Leave blank to show &quot;COMING SOON&quot;.
            </p>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploadingCover || uploadingAudio}
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Track'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Video Modal Component  
const VideoModal = ({ video, onClose, onSave, tracks }) => {
  const [form, setForm] = useState({
    title: video?.title || '',
    description: video?.description || '',
    category: video?.category || 'music_video',
    youtube_url: video?.youtube_url || '',
    thumbnail_url: video?.thumbnail_url || '',
    duration: video?.duration || 0,
    is_exclusive: video?.is_exclusive || false,
    linked_track_id: video?.linked_track_id || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (video) {
        await axiosAuth.put(`/api/admin/videos/${video.id}`, form);
      } else {
        await axiosAuth.post(`/api/admin/videos`, form);
      }
      onSave();
      onClose();
    } catch (e) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="font-['Bebas_Neue'] text-2xl">{video ? 'EDIT VIDEO' : 'ADD NEW VIDEO'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#262626] transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              required
            />
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows={3}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white resize-none"
            />
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({...form, category: e.target.value})}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
            >
              <option value="music_video">Music Video</option>
              <option value="interview">Interview</option>
              <option value="behind_the_scenes">Behind the Scenes</option>
              <option value="exclusive">Exclusive</option>
              <option value="live_session">Live Session</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">YouTube URL</label>
            <input
              type="text"
              value={form.youtube_url}
              onChange={(e) => setForm({...form, youtube_url: e.target.value})}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
            />
            <p className="text-xs text-[#D4AF37] mt-1">
              ✨ Thumbnail will be auto-generated from YouTube URL
            </p>
          </div>

          {/* Thumbnail URL field hidden - auto-generates from YouTube URL */}

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_exclusive}
                onChange={(e) => setForm({...form, is_exclusive: e.target.checked})}
                className="w-5 h-5 accent-[#D4AF37]"
              />
              <span>Exclusive (requires purchase to view)</span>
            </label>
          </div>

          {form.is_exclusive && (
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Link to Track Purchase</label>
              <select
                value={form.linked_track_id}
                onChange={(e) => setForm({...form, linked_track_id: e.target.value})}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              >
                <option value="">Select track...</option>
                {tracks.map(track => (
                  <option key={track.id} value={track.id}>{track.title}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Video'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Collection Modal Component
const CollectionModal = ({ collection, onClose, onSave, tracks }) => {
  const [form, setForm] = useState({
    name: collection?.name || '',
    description: collection?.description || '',
    cover_art_url: collection?.cover_art_url || '',
    track_ids: collection?.track_ids || []
  });
  const [saving, setSaving] = useState(false);

  const toggleTrack = (trackId) => {
    setForm(prev => ({
      ...prev,
      track_ids: prev.track_ids.includes(trackId)
        ? prev.track_ids.filter(id => id !== trackId)
        : [...prev.track_ids, trackId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (collection) {
        await axiosAuth.put(`/api/admin/collections/${collection.id}`, form);
      } else {
        await axiosAuth.post(`/api/admin/collections`, form);
      }
      onSave();
      onClose();
    } catch (e) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="font-['Bebas_Neue'] text-2xl">{collection ? 'EDIT COLLECTION' : 'CREATE COLLECTION'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#262626] transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Collection Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="e.g., Spiritual Journey, Late Night Vibes"
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              required
            />
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows={2}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white resize-none"
            />
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Cover Art URL</label>
            <input
              type="text"
              value={form.cover_art_url}
              onChange={(e) => setForm({...form, cover_art_url: e.target.value})}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
            />
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Select Tracks</label>
            <div className="max-h-48 overflow-y-auto bg-[#262626] border border-white/10">
              {tracks.map(track => (
                <label
                  key={track.id}
                  className="flex items-center gap-3 p-3 hover:bg-[#333] cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={form.track_ids.includes(track.id)}
                    onChange={() => toggleTrack(track.id)}
                    className="w-4 h-4 accent-[#D4AF37]"
                  />
                  <span>{track.title}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Collection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Perk Modal Component
const PerkModal = ({ perk, onClose, onSave }) => {
  const [form, setForm] = useState({
    perk_type: perk?.perk_type || 'bonus_track',
    title: perk?.title || '',
    description: perk?.description || '',
    content_url: perk?.content_url || '',
    discount_code: perk?.discount_code || '',
    discount_percent: perk?.discount_percent || 10
  });
  const [saving, setSaving] = useState(false);

  const perkTypes = [
    { value: 'bonus_track', label: 'Bonus Track' },
    { value: 'video_content', label: 'Exclusive Video' },
    { value: 'voice_note', label: 'Personal Voice Note' },
    { value: 'early_access', label: 'Early Access' },
    { value: 'merch_discount', label: 'Merch Discount' },
    { value: 'exclusive_content', label: 'Exclusive Content' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosAuth.post(`/api/admin/perks`, form);
      onSave();
      onClose();
    } catch (e) {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="font-['Bebas_Neue'] text-2xl">CREATE PERK</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#262626] transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Perk Type</label>
            <select
              value={form.perk_type}
              onChange={(e) => setForm({...form, perk_type: e.target.value})}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
            >
              {perkTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              placeholder="e.g., Behind-the-scenes studio footage"
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              required
            />
          </div>

          <div>
            <label className="text-sm text-[#A3A3A3] block mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows={2}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white resize-none"
            />
          </div>

          {form.perk_type === 'merch_discount' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#A3A3A3] block mb-2">Discount %</label>
                <input
                  type="number"
                  value={form.discount_percent}
                  onChange={(e) => setForm({...form, discount_percent: parseInt(e.target.value)})}
                  className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
                />
              </div>
              <div>
                <label className="text-sm text-[#A3A3A3] block mb-2">Discount Code</label>
                <input
                  type="text"
                  value={form.discount_code}
                  onChange={(e) => setForm({...form, discount_code: e.target.value})}
                  className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
                />
              </div>
            </div>
          )}

          {['bonus_track', 'video_content', 'voice_note', 'exclusive_content'].includes(form.perk_type) && (
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Content URL</label>
              <input
                type="text"
                value={form.content_url}
                onChange={(e) => setForm({...form, content_url: e.target.value})}
                placeholder="https://..."
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              />
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Create Perk'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ settings, onUpdate }) => {
  const [heroImage, setHeroImage] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [newsletterBg, setNewsletterBg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [radioEnabled, setRadioEnabled] = useState(true);
  const [updatingRadio, setUpdatingRadio] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Fetch current radio settings on mount
  useEffect(() => {
    const fetchRadioSettings = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/radio/settings`);
        setRadioEnabled(data.enabled ?? true);
      } catch (e) {
        console.error('Failed to fetch radio settings:', e);
      }
    };
    fetchRadioSettings();
  }, []);

  const handleRadioToggle = async (enabled) => {
    setUpdatingRadio(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/radio/settings`,
        { enabled },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      setRadioEnabled(enabled);
      alert(`Radio ${enabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert(`Failed to update radio: ${detail}`);
    } finally {
      setUpdatingRadio(false);
    }
  };

  const handleHeroUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/hero`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          withCredentials: true
        }
      );
      alert('Hero image updated successfully!');
      onUpdate();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      setHeroImage(null);
    }
  };

  const handleProfileImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/profile`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          withCredentials: true
        }
      );
      alert('Profile image updated successfully!');
      onUpdate();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      setProfileImage(null);
    }
  };

  const handleNewsletterBgUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/newsletter-bg`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          withCredentials: true
        }
      );
      alert('Newsletter background updated successfully!');
      onUpdate();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      setNewsletterBg(null);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    
    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters long!');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      alert('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert(`Failed to change password: ${detail}`);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-['Bebas_Neue'] text-2xl mb-6">SITE SETTINGS</h2>
        
        {/* Radio Toggle */}
        <div className="bg-[#1A1A1A] p-6 rounded-lg mb-6">
          <h3 className="font-medium text-white mb-4">Homepage Radio Bar</h3>
          <p className="text-sm text-[#A3A3A3] mb-4">
            Control the "YOUNG BLACK MAN INC. RADIO" bar that appears at the top of every page. 
            When enabled, visitors can listen to your full catalog in shuffle mode.
          </p>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleRadioToggle(!radioEnabled)}
              disabled={updatingRadio}
              className={`px-6 py-3 font-medium uppercase tracking-wider transition-colors disabled:opacity-50 ${
                radioEnabled 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {updatingRadio ? 'Updating...' : radioEnabled ? '🔊 Radio ON' : '🔇 Radio OFF'}
            </button>
            <span className="text-sm text-[#A3A3A3]">
              Current Status: <span className={radioEnabled ? 'text-green-400' : 'text-red-400'}>
                {radioEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </span>
          </div>
        </div>
        
        {/* Hero Image Upload */}
        <div className="bg-[#1A1A1A] p-6 rounded-lg">
          <h3 className="font-medium text-white mb-4">Hero / Background Image</h3>
          <p className="text-sm text-[#A3A3A3] mb-4">
            Upload a new background image for your homepage. Current image will be replaced.
          </p>
          
          <div className="space-y-4">
            {settings?.hero_image_url && (
              <div className="mb-4">
                <p className="text-xs text-[#A3A3A3] mb-2">Current Hero Image:</p>
                <img 
                  src={settings.hero_image_url} 
                  alt="Current hero" 
                  className="w-full max-w-md h-48 object-cover rounded"
                />
              </div>
            )}
            
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setHeroImage(e.target.files[0])}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white"
            />
            
            {heroImage && (
              <div className="flex gap-4">
                <button
                  onClick={() => handleHeroUpload(heroImage)}
                  disabled={uploading}
                  className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Hero Image'}
                </button>
                <button
                  onClick={() => setHeroImage(null)}
                  className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Profile Image Upload (About Section) */}
        <div className="bg-[#1A1A1A] p-6 rounded-lg mt-6">
          <h3 className="font-medium text-white mb-4">Profile Image (About Section)</h3>
          <p className="text-sm text-[#A3A3A3] mb-4">
            Upload your profile image that appears in the "About The Artist" section at the bottom of the homepage.
          </p>
          
          <div className="space-y-4">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setProfileImage(e.target.files[0])}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white"
            />
            
            {profileImage && (
              <div className="flex gap-4">
                <button
                  onClick={() => handleProfileImageUpload(profileImage)}
                  disabled={uploading}
                  className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Profile Image'}
                </button>
                <button
                  onClick={() => setProfileImage(null)}
                  className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Newsletter Background Image */}
        <div className="bg-[#1A1A1A] p-6 rounded-lg mt-6">
          <h3 className="font-medium text-white mb-4">Newsletter Background Image</h3>
          <p className="text-sm text-[#A3A3A3] mb-4">
            Upload the background image for the "Join The Movement" newsletter section at the bottom of the homepage.
          </p>
          
          <div className="space-y-4">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setNewsletterBg(e.target.files[0])}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white"
            />
            
            {newsletterBg && (
              <div className="flex gap-4">
                <button
                  onClick={() => handleNewsletterBgUpload(newsletterBg)}
                  disabled={uploading}
                  className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Newsletter Background'}
                </button>
                <button
                  onClick={() => setNewsletterBg(null)}
                  className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Artist Content (tagline, bio, socials) — editable, no code change needed */}
        <ArtistSettingsSection settings={settings} onUpdate={onUpdate} />

        {/* Password Change Section */}
        <div className="bg-[#1A1A1A] p-6 rounded-lg mt-6 border-2 border-[#D4AF37]/30">
          <h3 className="font-medium text-white mb-4 flex items-center gap-2">
            <span>🔒</span> Change Admin Password
          </h3>
          <p className="text-sm text-[#A3A3A3] mb-6">
            Keep your admin account secure by updating your password regularly. Use a strong password with at least 8 characters.
          </p>
          
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm text-[#A3A3A3] mb-2">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
                required
                minLength={6}
              />
            </div>
            
            <div>
              <label className="block text-sm text-[#A3A3A3] mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
                required
                minLength={8}
                placeholder="At least 8 characters"
              />
            </div>
            
            <div>
              <label className="block text-sm text-[#A3A3A3] mb-2">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
                required
                minLength={8}
                placeholder="Re-enter new password"
              />
            </div>
            
            <button
              type="submit"
              disabled={changingPassword}
              className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-medium uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Artist Settings Section (tagline, bio, social links) — admin-editable
const ArtistSettingsSection = ({ settings, onUpdate }) => {
  const [tagline, setTagline] = React.useState(settings?.artist_tagline || '');
  const [bio, setBio] = React.useState(settings?.artist_bio || '');
  const [socials, setSocials] = React.useState(settings?.artist_socials || {});
  const [saving, setSaving] = React.useState(false);

  // Reflect new props (when admin upload triggers onUpdate -> re-fetch settings)
  React.useEffect(() => {
    setTagline(settings?.artist_tagline || '');
    setBio(settings?.artist_bio || '');
    setSocials(settings?.artist_socials || {});
  }, [settings?.artist_tagline, settings?.artist_bio, settings?.artist_socials]);

  const socialFields = [
    { key: 'spotify', label: 'Spotify URL', placeholder: 'https://open.spotify.com/artist/...' },
    { key: 'apple_music', label: 'Apple Music URL', placeholder: 'https://music.apple.com/us/artist/...' },
    { key: 'youtube', label: 'YouTube URL', placeholder: 'https://youtube.com/@...' },
    { key: 'tiktok', label: 'TikTok URL', placeholder: 'https://tiktok.com/@...' },
    { key: 'instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/...' },
    { key: 'facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/...' },
    { key: 'soundcloud', label: 'SoundCloud URL', placeholder: 'https://soundcloud.com/...' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/artist`,
        { tagline, bio, socials },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      alert('Artist content updated successfully!');
      onUpdate && onUpdate();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert(`Failed to save: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#1A1A1A] p-6 rounded-lg mt-6" data-testid="artist-settings-section">
      <h3 className="font-medium text-white mb-2">Artist Content</h3>
      <p className="text-sm text-[#A3A3A3] mb-6">
        Edit your tagline, bio, and social links. These appear on the homepage hero + About section.
        Leave a social field empty to hide that icon on the site.
      </p>

      <div className="space-y-5">
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">Tagline (homepage hero subtitle)</label>
          <input
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="You Become What You Think About"
            className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
            data-testid="settings-tagline-input"
          />
        </div>

        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">Bio (About section)</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={6}
            placeholder="Tell visitors who you are…"
            className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none font-mono text-sm leading-relaxed"
            data-testid="settings-bio-input"
          />
          <p className="text-xs text-[#737373] mt-1">
            {bio.length} characters. Plain text only — line breaks are preserved.
          </p>
        </div>

        <div>
          <p className="text-sm text-[#A3A3A3] mb-3">Social Links</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {socialFields.map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className="block text-xs text-[#737373] mb-1 uppercase tracking-wider">{label}</label>
                <input
                  type="url"
                  value={socials[key] || ''}
                  onChange={(e) => setSocials({ ...socials, [key]: e.target.value })}
                  placeholder={placeholder}
                  className="w-full px-3 py-2 bg-[#262626] border border-white/10 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
                  data-testid={`settings-social-${key}-input`}
                />
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
          data-testid="save-artist-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Artist Content'}
        </button>
      </div>
    </div>
  );
};

// Beat Modal Component
const BeatModal = ({ beat, onClose, onSave }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({
    title: beat?.title || '',
    bpm: beat?.bpm || '',
    key: beat?.key || '',
    genre: beat?.genre || 'Hip Hop',
    mood: beat?.mood || '',
    duration: beat?.duration || 180,
    cover_art_url: beat?.cover_art_url || '',
    audio_url: beat?.audio_url || '',
    basic_lease_price: beat?.basic_lease_price || 29.99,
    premium_lease_price: beat?.premium_lease_price || 99.99,
    exclusive_rights_price: beat?.exclusive_rights_price || 299.99,
    is_active: beat?.is_active ?? true
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.title || !form.title.trim()) {
      alert('Beat title is required');
      return;
    }
    
    if (!form.audio_url) {
      alert('Please upload beat audio file');
      return;
    }
    
    setSaving(true);
    try {
      // Clean up form data - convert empty strings to null for optional int fields
      const cleanForm = {
        ...form,
        bpm: form.bpm === '' || form.bpm === null ? null : parseInt(form.bpm) || null,
        key: form.key === '' ? null : form.key,
        mood: form.mood === '' ? null : form.mood,
        duration: parseInt(form.duration) || 180
      };
      
      if (beat) {
        await axiosAuth.put(`/api/admin/beats/${beat.id}`, cleanForm);
      } else {
        await axiosAuth.post('/api/admin/beats', cleanForm);
      }
      onSave();
      onClose();
    } catch (e) {
      logger.error('Beat save error:', e);
      logger.error('Error response:', e.response);
      logger.error('Error data:', e.response?.data);
      
      let errorMsg = 'Failed to save beat';
      
      if (e.response?.data) {
        if (typeof e.response.data === 'string') {
          errorMsg = e.response.data;
        } else if (e.response.data.detail) {
          if (typeof e.response.data.detail === 'string') {
            errorMsg = e.response.data.detail;
          } else {
            errorMsg = JSON.stringify(e.response.data.detail);
          }
        } else {
          errorMsg = JSON.stringify(e.response.data);
        }
      } else if (e.message) {
        errorMsg = e.message;
      }
      
      alert(`Failed to save beat: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    // iOS-friendly format check for audio uploads (some iOS Safari versions
    // give an empty MIME type, so we also accept by extension)
    if (type === 'audio') {
      const allowedExts = ['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg', 'oga'];
      const fname = (file.name || '').toLowerCase();
      const ext = fname.includes('.') ? fname.split('.').pop() : '';
      const mime = (file.type || '').toLowerCase();
      const looksLikeAudio = mime.startsWith('audio/') || allowedExts.includes(ext);
      if (!looksLikeAudio) {
        alert('This audio format is not supported on iPhone. Please upload MP3, M4A, AAC, or WAV.');
        return;
      }
    }

    // Check file size
    const maxSize = type === 'audio' ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for audio, 10MB for images
    if (file.size > maxSize) {
      const maxMB = type === 'audio' ? 50 : 10;
      alert(`File too large. Maximum size is ${maxMB}MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoint = type === 'audio' ? '/api/admin/upload/beat-audio' : '/api/admin/upload/beat-cover';
      const response = await axiosAuth.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: type === 'audio' ? 120000 : 60000, // 2 min for audio, 1 min for images
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          logger.debug(`${type} upload progress: ${percentCompleted}%`);
        }
      });
      const fieldName = type === 'audio' ? 'audio_url' : 'cover_art_url';
      const fullUrl = `${API}${response.data.url}`;
      setForm(prev => ({ ...prev, [fieldName]: fullUrl }));
      alert(`${type === 'audio' ? 'Audio' : 'Cover'} uploaded successfully!`);
    } catch (e) {
      logger.error(`${type} upload error:`, e);
      if (e.code === 'ECONNABORTED') {
        alert('Upload timeout. Please try with a smaller file or check your internet connection.');
      } else {
        alert(`Upload failed: ${e.response?.data?.detail || e.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 animate-fade-in">
      <div className="bg-[#1A1A1A] w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#1A1A1A] z-10">
          <h2 className="font-['Bebas_Neue'] text-2xl">{beat ? 'EDIT BEAT' : 'ADD NEW BEAT'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/5 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Beat Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
                placeholder="Enter beat title"
              />
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Genre *</label>
              <select
                value={form.genre}
                onChange={(e) => setForm({ ...form, genre: e.target.value })}
                required
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              >
                <option>Hip Hop</option>
                <option>R&B</option>
                <option>Trap</option>
                <option>Pop</option>
                <option>Drill</option>
                <option>Instrumental</option>
              </select>
            </div>
          </div>

          {/* Musical Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">BPM</label>
              <input
                type="number"
                value={form.bpm}
                onChange={(e) => setForm({ ...form, bpm: parseInt(e.target.value) || '' })}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
                placeholder="120"
              />
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Key</label>
              <input
                type="text"
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
                placeholder="C Minor"
              />
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Mood</label>
              <input
                type="text"
                value={form.mood}
                onChange={(e) => setForm({ ...form, mood: e.target.value })}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
                placeholder="Dark, Energetic, Chill"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Basic Lease ($)</label>
              <input
                type="number"
                step="0.01"
                value={form.basic_lease_price}
                onChange={(e) => setForm({ ...form, basic_lease_price: parseFloat(e.target.value) })}
                required
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              />
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Premium Lease ($)</label>
              <input
                type="number"
                step="0.01"
                value={form.premium_lease_price}
                onChange={(e) => setForm({ ...form, premium_lease_price: parseFloat(e.target.value) })}
                required
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              />
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Exclusive Rights ($)</label>
              <input
                type="number"
                step="0.01"
                value={form.exclusive_rights_price}
                onChange={(e) => setForm({ ...form, exclusive_rights_price: parseFloat(e.target.value) })}
                required
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 focus:border-[#D4AF37] focus:outline-none text-white"
              />
            </div>
          </div>

          {/* File Uploads */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Beat Audio File</label>
              <input
                type="file"
                accept=".mp3,.m4a,.wav,.aac,.flac,audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/aac,audio/*"
                onChange={(e) => handleFileUpload(e.target.files[0], 'audio')}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white"
                data-testid="beat-audio-upload-input"
              />
              {form.audio_url && <p className="text-xs text-green-400 mt-1">✓ Audio uploaded</p>}
              <p className="text-xs text-[#737373] mt-1">MP3, M4A, AAC, or WAV. Max 50MB.</p>
            </div>
            <div>
              <label className="text-sm text-[#A3A3A3] block mb-2">Cover Art</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e.target.files[0], 'cover')}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white"
              />
              {form.cover_art_url && <p className="text-xs text-green-400 mt-1">✓ Cover uploaded</p>}
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="w-5 h-5"
            />
            <label className="text-sm text-white">Beat is active (visible on Beats page)</label>
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : uploading ? 'Uploading...' : beat ? 'Update Beat' : 'Create Beat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Download Modal Component
const DownloadModal = ({ download, onClose, onSave }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({
    title: download?.title || '',
    description: download?.description || '',
    category: download?.category || 'books',
    book_category: download?.book_category || '',
    pdf_url: download?.pdf_url || '',
    cover_image_url: download?.cover_image_url || '',
    file_size_mb: download?.file_size_mb || 0,
    price: download?.price || 0,
    full_text: download?.full_text || '',
    preview_pages: download?.preview_pages || 2,
    is_active: download?.is_active ?? true,
    shopify_product_id: download?.shopify_product_id || '',
    shopify_variant_id: download?.shopify_variant_id || '',
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.title.trim()) {
      alert('Title is required');
      return;
    }
    
    if (!form.pdf_url) {
      alert('Please upload a PDF file');
      return;
    }
    
    setSaving(true);
    try {
      if (download) {
        await axiosAuth.put(`/api/admin/downloads/${download.id}`, form);
      } else {
        await axiosAuth.post('/api/admin/downloads', form);
      }
      onSave();
      onClose();
    } catch (e) {
      console.error('Download save error:', e);
      alert(`Failed to save: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const maxSize = type === 'pdf' ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for PDFs, 10MB for images
    if (file.size > maxSize) {
      const maxMB = type === 'pdf' ? 50 : 10;
      alert(`File too large. Maximum size is ${maxMB}MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoint = type === 'pdf' ? '/api/admin/upload/pdf' : '/api/admin/upload/cover';
      const response = await axiosAuth.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: type === 'pdf' ? 120000 : 60000,
      });
      
      if (type === 'pdf') {
        setForm(prev => ({
          ...prev,
          pdf_url: response.data.url,
          file_size_mb: response.data.file_size_mb
        }));
      } else {
        setForm(prev => ({ ...prev, cover_image_url: `${API}${response.data.url}` }));
      }
      
      alert(`${type === 'pdf' ? 'PDF' : 'Cover image'} uploaded successfully!`);
    } catch (e) {
      console.error(`${type} upload error:`, e);
      alert(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const bookCategories = [
    { value: '', label: 'None (Not a book)' },
    { value: 'finances', label: 'Finances' },
    { value: 'business', label: 'Business' },
    { value: 'spiritual', label: 'Spiritual' },
    { value: 'childrens_books', label: "Children's Books" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 animate-fade-in">
      <div className="bg-[#1A1A1A] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#1A1A1A] z-10">
          <h2 className="font-['Bebas_Neue'] text-2xl">
            {download ? 'EDIT PDF DOWNLOAD' : 'ADD PDF DOWNLOAD'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/5 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
              placeholder="e.g., Financial Freedom Guide"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
              placeholder="Brief description of the PDF..."
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Category *</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
              required
            >
              <option value="books">Books</option>
              <option value="materials">Materials</option>
              <option value="guides">Guides</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Book Category (only shown when category is 'books') */}
          {form.category === 'books' && (
            <div>
              <label className="block text-sm font-medium text-white mb-2">Book Category</label>
              <select
                value={form.book_category}
                onChange={(e) => setForm({ ...form, book_category: e.target.value })}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
              >
                {bookCategories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Price (USD) *
              <span className="text-xs text-[#A3A3A3] ml-2">Set to $0 for free downloads</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
              placeholder="9.99"
            />
            <p className="text-xs text-[#A3A3A3] mt-2">
              Suggested: Short guides $2.99-$7.99, Full books $9.99-$19.99, Children's books $4.99-$9.99
            </p>
          </div>

          {/* Full Text for AI Audiobook */}
          {form.category === 'books' && (
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Full Text (for AI Audiobook)
                <span className="text-xs text-[#A3A3A3] ml-2">Tier 2 & 3 subscribers get AI read-aloud access</span>
              </label>
              <textarea
                value={form.full_text}
                onChange={(e) => setForm({ ...form, full_text: e.target.value })}
                rows={8}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none transition-colors resize-none font-mono text-sm"
                placeholder="Paste the full text of the book here for AI text-to-speech generation..."
              />
              <p className="text-xs text-[#A3A3A3] mt-2">
                📢 This text will be used to generate an AI audiobook. Paste the complete book text here.
              </p>
            </div>
          )}

          {/* PDF Upload */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">PDF File *</label>
            {form.pdf_url ? (
              <div className="flex items-center gap-4 p-4 bg-[#262626] border border-green-500/20">
                <div className="flex-1">
                  <p className="text-sm text-green-400">PDF uploaded successfully!</p>
                  <p className="text-xs text-[#A3A3A3] mt-1">Size: {form.file_size_mb?.toFixed(2)} MB</p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, pdf_url: '', file_size_mb: 0 }))}
                  className="px-4 py-2 text-red-400 hover:bg-red-400/10 transition-colors"
                >
                  Remove
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => handleFileUpload(e.target.files[0], 'pdf')}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#D4AF37] file:text-black file:cursor-pointer hover:file:bg-[#E5C048]"
              />
            )}
          </div>

          {/* Cover Image Upload (optional) */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Cover Image (optional)</label>
            {form.cover_image_url ? (
              <div className="space-y-2">
                <img src={form.cover_image_url} alt="Cover" className="w-40 h-56 object-cover rounded" />
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, cover_image_url: '' }))}
                  className="px-4 py-2 text-red-400 hover:bg-red-400/10 transition-colors"
                >
                  Remove Cover
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e.target.files[0], 'cover')}
                className="w-full px-4 py-3 bg-[#262626] border border-white/10 text-white file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#D4AF37] file:text-black file:cursor-pointer hover:file:bg-[#E5C048]"
              />
            )}
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="w-5 h-5 bg-[#262626] border-white/10 text-[#D4AF37] focus:ring-[#D4AF37]"
            />
            <label htmlFor="is_active" className="text-sm text-white">
              Active (visible to users)
            </label>
          </div>

          {/* Shopify Buy Button mapping (manual override) */}
          <div className="border-t border-white/10 pt-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Shopify Buy Button (optional)</p>
            <p className="text-xs text-[#737373] mb-3">
              When set, this book renders a Shopify BUY button instead of the legacy Stripe checkout. Use the "Auto-Map Books to Shopify" button in the tab toolbar for bulk mapping — this field is for manual overrides.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Shopify product ID</label>
                <input
                  type="text"
                  value={form.shopify_product_id}
                  onChange={(e) => setForm({ ...form, shopify_product_id: e.target.value.trim() })}
                  placeholder="e.g. 15879444267089"
                  className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded font-mono text-sm"
                  data-testid="book-shopify-product-id-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Shopify variant ID (optional)</label>
                <input
                  type="text"
                  value={form.shopify_variant_id}
                  onChange={(e) => setForm({ ...form, shopify_variant_id: e.target.value.trim() })}
                  placeholder="(optional)"
                  className="w-full mt-1 px-3 py-2 bg-black border border-white/10 text-white rounded font-mono text-sm"
                  data-testid="book-shopify-variant-id-input"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-white/20 text-white hover:border-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : uploading ? 'Uploading...' : download ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===================== AD SLOT MODAL =====================
const AdSlotModal = ({ slot, onClose, onSave }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const isEdit = !!slot;
  const [form, setForm] = useState({
    placement: slot?.placement || 'top_banner',
    slot_name: slot?.slot_name || '',
    description: slot?.description || '',
    dimensions: slot?.dimensions || '',
    price_per_month: slot?.price_per_month ?? 100,
    max_slots: slot?.max_slots ?? 1,
    is_active: slot?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.slot_name || !form.placement || !form.dimensions) {
      alert('Name, placement, and dimensions are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      };
      const payload = {
        ...form,
        price_per_month: parseFloat(form.price_per_month) || 0,
        max_slots: parseInt(form.max_slots) || 1,
      };
      if (isEdit) {
        await axios.put(`${API}/api/admin/ad-slots/${slot.id}`, payload, config);
      } else {
        await axios.post(`${API}/api/admin/ad-slots`, payload, config);
      }
      onSave();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Save failed';
      alert(`Save failed: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#1A1A1A] rounded-lg max-w-2xl w-full p-6 my-8" data-testid="ad-slot-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-['Bebas_Neue'] text-2xl">{isEdit ? 'EDIT AD SLOT' : 'CREATE AD SLOT'}</h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Placement *">
            <select
              required
              value={form.placement}
              onChange={(e) => setForm({ ...form, placement: e.target.value })}
              className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
              data-testid="ad-slot-placement"
            >
              <option value="top_banner">Top Rotating Banner (every page)</option>
              <option value="homepage_hero">Homepage Hero Banner</option>
              <option value="beats_header">Beats Page Header</option>
              <option value="books_sponsored">Sponsored Book Card (PDF Books)</option>
              <option value="music_sidebar">Music Page Sidebar</option>
              <option value="video_banner">Videos Page Banner</option>
              <option value="bulletin_sidebar">Bulletin Sidebar</option>
              <option value="footer_banner">Footer Banner</option>
            </select>
          </Field>

          <Field label="Slot Name *">
            <input
              required
              value={form.slot_name}
              onChange={(e) => setForm({ ...form, slot_name: e.target.value })}
              placeholder="e.g., Top Rotating Banner"
              className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
              data-testid="ad-slot-name"
            />
          </Field>

          <Field label="Description">
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe where this ad appears and who it's for..."
              className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
              data-testid="ad-slot-description"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Dimensions *">
              <input
                required
                value={form.dimensions}
                onChange={(e) => setForm({ ...form, dimensions: e.target.value })}
                placeholder="728x90"
                className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                data-testid="ad-slot-dimensions"
              />
            </Field>
            <Field label="Max Slots *">
              <input
                required
                type="number"
                min="1"
                value={form.max_slots}
                onChange={(e) => setForm({ ...form, max_slots: e.target.value })}
                className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                data-testid="ad-slot-max"
              />
            </Field>
          </div>

          <Field label="Price per month (USD) *">
            <input
              required
              type="number"
              step="0.01"
              min="0"
              value={form.price_per_month}
              onChange={(e) => setForm({ ...form, price_per_month: e.target.value })}
              className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
              data-testid="ad-slot-price"
            />
          </Field>

          <label className="flex items-center gap-3 text-white text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="w-4 h-4 accent-[#D4AF37]"
              data-testid="ad-slot-active"
            />
            Active (advertisers can book this slot)
          </label>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-[#262626] text-white hover:bg-[#262626] rounded uppercase tracking-wider text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] disabled:opacity-50 rounded"
              data-testid="ad-slot-save"
            >
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Slot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs text-[#A3A3A3] uppercase tracking-wider mb-2">{label}</span>
    {children}
  </label>
);

// ===================== ADS CONTROL PANEL (kill-switch + auto-expire) =====================
const AdsControlPanel = ({ API, onChange }) => {
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axiosAuth.get(`${API}/api/admin/ads/kill-switch`)
      .then(({ data }) => setPaused(!!data.all_ads_paused))
      .catch(() => {});
  }, [API]);

  const togglePause = async () => {
    setBusy(true);
    try {
      const { data } = await axiosAuth.post(`${API}/api/admin/ads/kill-switch`, { pause: !paused });
      setPaused(!!data.all_ads_paused);
      onChange?.();
    } catch (e) {
      alert('Failed to toggle kill switch: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const runExpire = async () => {
    setBusy(true);
    try {
      const { data } = await axiosAuth.post(`${API}/api/admin/ads/auto-expire`);
      alert(data.message || 'Done');
      onChange?.();
    } catch (e) {
      alert('Failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-5 mb-6 grid sm:grid-cols-2 gap-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-white font-medium">Pause All Ads (master kill-switch)</p>
          <p className="text-xs text-[#737373] mt-1">Hides every ad on the public site instantly. Bookings remain on file.</p>
          <p className={`text-xs mt-2 ${paused ? 'text-red-400' : 'text-green-400'}`}>
            Status: {paused ? 'PAUSED — no ads visible to public' : 'Ads live (when approved + paid)'}
          </p>
        </div>
        <button
          onClick={togglePause}
          disabled={busy}
          data-testid="ads-kill-switch"
          className={`shrink-0 px-4 py-2 rounded uppercase tracking-wider text-sm font-medium ${paused ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'}`}
        >
          {paused ? 'Resume' : 'Pause All'}
        </button>
      </div>
      <div className="flex items-center justify-between gap-4 sm:border-l sm:border-[#262626] sm:pl-4">
        <div>
          <p className="text-sm text-white font-medium">Auto-Expire Old Bookings</p>
          <p className="text-xs text-[#737373] mt-1">Marks any booking past its end_date as expired. Run anytime — idempotent.</p>
        </div>
        <button
          onClick={runExpire}
          disabled={busy}
          data-testid="ads-auto-expire"
          className="shrink-0 px-4 py-2 bg-[#262626] text-white rounded uppercase tracking-wider text-sm hover:bg-[#333]"
        >
          Run Now
        </button>
      </div>
    </div>
  );
};

export default AdminPage;
